import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Image, Plus, Edit, Trash2, Eye, MousePointer, Upload, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const AdBannersPage = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image_url: '',
    link_url: '',
    placement: 'directory_sidebar',
    start_date: '',
    end_date: '',
    status: 'active'
  });

  const placements = [
    { value: 'directory_sidebar', label: 'Annuaire - Sidebar', size: '300 x 250 px', ratio: 'Rectangle moyen' },
    { value: 'directory_top', label: 'Annuaire - Haut de page', size: '1200 x 120 px', ratio: 'Leaderboard large' },
    { value: 'directory_bottom', label: 'Annuaire - Bas de page', size: '728 x 90 px', ratio: 'Leaderboard' },
    { value: 'homepage_hero', label: 'Page d\'accueil - Hero', size: '1920 x 400 px', ratio: 'Bannière hero' },
    { value: 'formations_sidebar', label: 'Formations - Sidebar', size: '300 x 250 px', ratio: 'Rectangle moyen' },
    { value: 'articles_banner', label: 'Articles - Bannière', size: '728 x 90 px', ratio: 'Leaderboard' },
  ];

  const getPlacementInfo = (val) => placements.find(p => p.value === val);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ad-banners`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBanners(data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des bannières');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const url = editingBanner
        ? `${process.env.REACT_APP_BACKEND_URL}/api/ad-banners/${editingBanner.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/ad-banners`;
      
      const method = editingBanner ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingBanner ? 'Bannière modifiée' : 'Bannière créée');
        setModalOpen(false);
        resetForm();
        fetchBanners();
      } else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Supprimer cette bannière ?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ad-banners/${bannerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Bannière supprimée');
        fetchBanners();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title,
      image_url: banner.image_url,
      link_url: banner.link_url || '',
      placement: banner.placement,
      start_date: banner.start_date || '',
      end_date: banner.end_date || '',
      status: banner.status
    });
    setModalOpen(true);
  };

  const resetForm = () => {
    setEditingBanner(null);
    setFormData({
      title: '',
      image_url: '',
      link_url: '',
      placement: 'directory_sidebar',
      start_date: '',
      end_date: '',
      status: 'active'
    });
  };

  const getCTR = (banner) => {
    if (banner.views_count === 0) return 0;
    return ((banner.clicks_count / banner.views_count) * 100).toFixed(2);
  };

  const statusBadges = {
    active: { label: 'Active', color: 'bg-green-100 text-green-800' },
    inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-800' },
    expired: { label: 'Expirée', color: 'bg-red-100 text-red-800' }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
              <Image className="text-[#1e3a5f]" size={32} />
              Bannières Publicitaires
            </h1>
            <p className="text-gray-600 mt-1">Gérez les bannières dans l&apos;annuaire avec tracking des performances</p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setModalOpen(true);
            }}
            className="bg-[#1e3a5f] hover:bg-[#162d4a] text-white"
          >
            <Plus size={18} className="mr-2" />
            Nouvelle Bannière
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 text-gray-900">
            <div className="text-2xl font-bold">{banners.length}</div>
            <div className="text-sm opacity-90">Total Bannières</div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 text-gray-900">
            <div className="text-2xl font-bold">{banners.filter(b => b.status === 'active').length}</div>
            <div className="text-sm opacity-90">Actives</div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 text-gray-900">
            <div className="text-2xl font-bold">{banners.reduce((sum, b) => sum + b.views_count, 0)}</div>
            <div className="text-sm opacity-90">Vues Totales</div>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl p-5 text-white">
            <div className="text-2xl font-bold">{banners.reduce((sum, b) => sum + b.clicks_count, 0)}</div>
            <div className="text-sm opacity-90">Clics Totaux</div>
          </div>
        </div>

        {/* Banners List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {banners.length === 0 ? (
            <div className="text-center py-16">
              <Image className="mx-auto text-gray-400 mb-4" size={64} />
              <h3 className="text-xl font-bold text-gray-600 mb-2">Aucune bannière</h3>
              <p className="text-gray-500">Créez votre première bannière publicitaire</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {banners.map((banner) => {
                const statusBadge = statusBadges[banner.status] || statusBadges.active;
                const placement = placements.find(p => p.value === banner.placement);
                
                return (
                  <div key={banner.id} className="p-5 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-4">
                      {/* Preview */}
                      <div className="w-32 h-32 rounded-lg overflow-hidden border-2 border-gray-200 flex-shrink-0">
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${banner.image_url}`}
                          alt={banner.title}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">{banner.title}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusBadge.color}`}>
                            {statusBadge.label}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-1">
                          <span className="font-medium">Emplacement:</span> {placement?.label}
                          {placement?.size && <span className="ml-2 text-xs text-gray-400">({placement.size})</span>}
                        </p>
                        <p className="text-xs text-blue-600 mb-3">
                          <a href={placement?.value === 'directory_sidebar' || placement?.value === 'directory_top' || placement?.value === 'directory_bottom' ? '/annuaire' : placement?.value === 'homepage_hero' ? '/' : placement?.value === 'formations_sidebar' ? '/formations' : '/articles'} target="_blank" rel="noreferrer" className="hover:underline flex items-center gap-1">
                            <ExternalLink size={12} /> Voir sur la page : {placement?.value === 'directory_sidebar' || placement?.value === 'directory_top' || placement?.value === 'directory_bottom' ? '/annuaire' : placement?.value === 'homepage_hero' ? '/' : placement?.value === 'formations_sidebar' ? '/formations' : '/articles'}
                          </a>
                        </p>
                        
                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-4 bg-gray-50 p-3 rounded-lg">
                          <div>
                            <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                              <Eye size={14} />
                              <span>Vues</span>
                            </div>
                            <div className="text-xl font-bold text-gray-900">{banner.views_count}</div>
                          </div>
                          <div>
                            <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                              <MousePointer size={14} />
                              <span>Clics</span>
                            </div>
                            <div className="text-xl font-bold text-gray-900">{banner.clicks_count}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-600 mb-1">CTR</div>
                            <div className="text-xl font-bold text-[#1e3a5f]">{getCTR(banner)}%</div>
                          </div>
                        </div>

                        {/* Dates */}
                        {(banner.start_date || banner.end_date) && (
                          <div className="mt-3 text-xs text-gray-500">
                            {banner.start_date && <span>Début: {new Date(banner.start_date).toLocaleDateString('fr-FR')}</span>}
                            {banner.start_date && banner.end_date && <span className="mx-2">→</span>}
                            {banner.end_date && <span>Fin: {new Date(banner.end_date).toLocaleDateString('fr-FR')}</span>}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditModal(banner)}
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(banner.id)}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingBanner ? 'Modifier la bannière' : 'Nouvelle bannière publicitaire'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Titre</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label>Image de la banniere</Label>
                {(() => {
                  const info = getPlacementInfo(formData.placement);
                  return info ? (
                    <div className="flex items-center gap-2 mb-2 p-2 bg-blue-50 rounded-lg border border-blue-100">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0"><rect x="1" y="3" width="14" height="10" rx="1.5" stroke="#35AFE4" strokeWidth="1.5"/><path d="M4 8h8M8 5v6" stroke="#35AFE4" strokeWidth="1" strokeLinecap="round"/></svg>
                      <span className="text-xs text-blue-700">
                        Taille recommandee pour <strong>{info.label}</strong> : <strong>{info.size}</strong> ({info.ratio})
                      </span>
                    </div>
                  ) : null;
                })()}
                <label className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 transition-colors">
                  <Upload size={20} className="text-gray-400" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700">{formData.image_url ? 'Changer l\'image' : 'Uploader une image'}</p>
                    <p className="text-xs text-gray-400">JPG, PNG, WebP - Max 5 Mo</p>
                  </div>
                  <input type="file" accept="image/*" className="hidden" data-testid="banner-image-upload" onChange={async (e) => {
                    if (!e.target.files[0]) return;
                    const fd = new FormData();
                    fd.append('file', e.target.files[0]);
                    try {
                      const token = localStorage.getItem('token');
                      const res = await axios.post(`${API}/upload/banner`, fd, { headers: { Authorization: `Bearer ${token}` } });
                      setFormData(prev => ({ ...prev, image_url: res.data.url }));
                      toast.success('Image uploadée');
                    } catch { toast.error("Erreur d'upload"); }
                  }} />
                </label>
                {formData.image_url && (
                  <div className="mt-2 w-full h-32 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${formData.image_url}`} alt="Preview" className="w-full h-full object-contain" onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                )}
              </div>

              <div>
                <Label>Lien de destination (optionnel)</Label>
                <Input
                  value={formData.link_url}
                  onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                  placeholder="https://exemple.com"
                />
              </div>

              <div>
                <Label>Emplacement</Label>
                <select
                  value={formData.placement}
                  onChange={(e) => setFormData({ ...formData, placement: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="placement-select"
                >
                  {placements.map(p => (
                    <option key={p.value} value={p.value}>{p.label} — {p.size}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date de début (optionnel)</Label>
                  <Input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Date de fin (optionnel)</Label>
                  <Input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label>Statut</Label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setModalOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button type="submit" className="flex-1 bg-[#1e3a5f] hover:bg-[#162d4a] text-white">
                  {editingBanner ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdBannersPage;